import * as THREE from 'three'
import { OrbitControls } from 'three/addons/controls/OrbitControls.js'
import type { CameraMode } from '../types'
import { ANCHORS, WORLD_BOUNDS } from './geo'
import { createCarriedFlight, type CarriedFlight } from './carriedFlight'

export interface ModeControls {
  mode: CameraMode
  setMode: (mode: CameraMode) => void
  update: (dt: number) => void
  dispose: () => void
  setExplorePose: (position: THREE.Vector3, target: THREE.Vector3) => void
  getExploreTarget: () => THREE.Vector3
  /** Orbit look-target, or the carried-flight position while flying. */
  getFocusPoint: () => THREE.Vector3
  getFlight: () => CarriedFlight
  /** Flies to a target at an explicit distance and a fixed, flattering pitch. */
  frameOn: (target: THREE.Vector3, distance: number) => void
  focusOn: (target: THREE.Vector3) => void
  resetNorth: () => void
  topDown: () => void
  enabled: boolean
  onModeChange: ((mode: CameraMode) => void) | null
}

export function createModeControls(
  camera: THREE.PerspectiveCamera,
  dom: HTMLElement,
  getHeight: (x: number, z: number) => number,
  scene: THREE.Scene,
): ModeControls {
  let mode: CameraMode = 'explore'
  let enabled = true
  let onModeChange: ((mode: CameraMode) => void) | null = null

  const flight = createCarriedFlight({
    getHeight,
    scene,
    bounds: WORLD_BOUNDS,
  })

  const orbit = new OrbitControls(camera, dom)
  orbit.enableDamping = true
  orbit.dampingFactor = 0.06
  orbit.enablePan = true
  orbit.enableRotate = true
  orbit.enableZoom = true
  orbit.zoomToCursor = true
  // Full map play: left-drag pans the terrain; right-drag orbits freely.
  orbit.screenSpacePanning = true
  orbit.panSpeed = 1.25
  orbit.rotateSpeed = 0.7
  orbit.zoomSpeed = 1.2
  orbit.mouseButtons.LEFT = THREE.MOUSE.PAN
  orbit.mouseButtons.MIDDLE = THREE.MOUSE.DOLLY
  orbit.mouseButtons.RIGHT = THREE.MOUSE.ROTATE
  orbit.touches.ONE = THREE.TOUCH.PAN
  orbit.touches.TWO = THREE.TOUCH.DOLLY_ROTATE
  orbit.minDistance = 6
  orbit.maxDistance = 900
  orbit.minPolarAngle = 0.02
  orbit.maxPolarAngle = Math.PI * 0.495
  orbit.target.set(-10, 6, 0)
  camera.position.set(250, 220, 250)
  orbit.update()

  const keys = new Set<string>()
  const euler = new THREE.Euler(0, 0, 0, 'YXZ')
  let unlocked = false

  let pointerSteering = false
  let pointerId: number | null = null
  let lastPointerX = 0
  let lastPointerY = 0
  let dragTurn = 0
  let dragClimb = 0

  const onKeyDown = (e: KeyboardEvent) => keys.add(e.code)
  const onKeyUp = (e: KeyboardEvent) => keys.delete(e.code)

  const onClick = () => {
    if (!enabled || mode !== 'walk') return
    if (!unlocked) void dom.requestPointerLock()
  }

  const onLockChange = () => {
    unlocked = document.pointerLockElement === dom
  }

  const onMouseMove = (e: MouseEvent) => {
    if (!enabled || mode !== 'walk' || !unlocked) return
    euler.setFromQuaternion(camera.quaternion)
    euler.y -= e.movementX * 0.0022
    euler.x -= e.movementY * 0.0022
    euler.x = Math.max(-Math.PI / 3, Math.min(Math.PI / 3, euler.x))
    camera.quaternion.setFromEuler(euler)
  }

  const onPointerDown = (e: PointerEvent) => {
    if (!enabled || mode !== 'flight' || e.button !== 0) return
    pointerSteering = true
    pointerId = e.pointerId
    lastPointerX = e.clientX
    lastPointerY = e.clientY
    dragTurn = 0
    dragClimb = 0
    try {
      dom.setPointerCapture(e.pointerId)
    } catch {
      // Capture can fail on some browsers mid-gesture; drag still works via move.
    }
  }

  const onPointerMove = (e: PointerEvent) => {
    if (!pointerSteering || mode !== 'flight') return
    if (pointerId != null && e.pointerId !== pointerId) return
    const dx = e.clientX - lastPointerX
    const dy = e.clientY - lastPointerY
    lastPointerX = e.clientX
    lastPointerY = e.clientY
    dragTurn -= dx * 0.014
    dragClimb -= dy * 0.012
  }

  const endPointerSteer = (e: PointerEvent) => {
    if (pointerId != null && e.pointerId !== pointerId) return
    pointerSteering = false
    pointerId = null
    dragTurn = 0
    dragClimb = 0
  }

  window.addEventListener('keydown', onKeyDown)
  window.addEventListener('keyup', onKeyUp)
  dom.addEventListener('click', onClick)
  document.addEventListener('pointerlockchange', onLockChange)
  document.addEventListener('mousemove', onMouseMove)
  dom.addEventListener('pointerdown', onPointerDown)
  dom.addEventListener('pointermove', onPointerMove)
  dom.addEventListener('pointerup', endPointerSteer)
  dom.addEventListener('pointercancel', endPointerSteer)

  const forward = new THREE.Vector3()
  const right = new THREE.Vector3()
  const flyCamera = new THREE.Vector3()
  const flyTarget = new THREE.Vector3()
  let flying = false

  orbit.listenToKeyEvents(window)
  orbit.addEventListener('start', () => {
    flying = false
  })

  const clampExploreTarget = () => {
    const clampedX = Math.max(
      WORLD_BOUNDS.minX,
      Math.min(WORLD_BOUNDS.maxX, orbit.target.x),
    )
    const clampedZ = Math.max(
      WORLD_BOUNDS.minZ,
      Math.min(WORLD_BOUNDS.maxZ, orbit.target.z),
    )
    const shiftX = clampedX - orbit.target.x
    const shiftZ = clampedZ - orbit.target.z
    if (shiftX !== 0 || shiftZ !== 0) {
      orbit.target.x = clampedX
      orbit.target.z = clampedZ
      camera.position.x += shiftX
      camera.position.z += shiftZ
    }
  }

  const focusOn = (target: THREE.Vector3) => {
    const offset = camera.position.clone().sub(orbit.target)
    const distance = Math.max(orbit.minDistance * 1.5, offset.length() * 0.52)
    offset.normalize().multiplyScalar(distance)
    flyTarget.copy(target)
    flyTarget.y = Math.max(target.y, getHeight(target.x, target.z))
    flyCamera.copy(flyTarget).add(offset)
    flying = true
  }

  const frameOn = (target: THREE.Vector3, distance: number) => {
    const current = camera.position.clone().sub(orbit.target)
    const azimuth = Math.atan2(current.z, current.x)
    const pitch = THREE.MathUtils.degToRad(32)
    const radius = THREE.MathUtils.clamp(
      distance,
      orbit.minDistance * 1.4,
      orbit.maxDistance * 0.9,
    )
    flyTarget.copy(target)
    flyTarget.y = Math.max(target.y, getHeight(target.x, target.z))
    flyCamera
      .set(
        Math.cos(azimuth) * Math.cos(pitch),
        Math.sin(pitch),
        Math.sin(azimuth) * Math.cos(pitch),
      )
      .multiplyScalar(radius)
      .add(flyTarget)
    flying = true
  }

  const resetNorth = () => {
    const offset = camera.position.clone().sub(orbit.target)
    const horizontal = Math.hypot(offset.x, offset.z)
    flyTarget.copy(orbit.target)
    flyCamera.set(orbit.target.x, orbit.target.y + offset.y, orbit.target.z + horizontal)
    flying = true
  }

  const topDown = () => {
    const distance = camera.position.distanceTo(orbit.target)
    flyTarget.copy(orbit.target)
    flyCamera.set(orbit.target.x, orbit.target.y + distance, orbit.target.z + 0.01)
    flying = true
  }

  const applyWalkBounds = () => {
    camera.position.x = Math.max(
      WORLD_BOUNDS.minX + 2,
      Math.min(WORLD_BOUNDS.maxX - 2, camera.position.x),
    )
    camera.position.z = Math.max(
      WORLD_BOUNDS.minZ + 2,
      Math.min(WORLD_BOUNDS.maxZ - 2, camera.position.z),
    )
    const ground = getHeight(camera.position.x, camera.position.z)
    camera.position.y = ground + 1.8
  }

  const headingFromCamera = () => {
    camera.getWorldDirection(forward)
    forward.y = 0
    if (forward.lengthSq() < 1e-6) return 0
    forward.normalize()
    return Math.atan2(forward.x, -forward.z)
  }

  const restoreExploreNearFlight = () => {
    const pos = flight.position
    const ground = getHeight(pos.x, pos.z)
    const target = new THREE.Vector3(pos.x, ground, pos.z)
    const back = 48
    const cam = new THREE.Vector3(
      pos.x - Math.sin(flight.heading) * back,
      ground + 32,
      pos.z + Math.cos(flight.heading) * back,
    )
    camera.position.copy(cam)
    orbit.target.copy(target)
    orbit.update()
  }

  const setMode = (next: CameraMode) => {
    if (document.pointerLockElement === dom) document.exitPointerLock()
    const prev = mode
    if (prev === 'flight' && next !== 'flight') {
      flight.exit()
      restoreExploreNearFlight()
    }
    mode = next
    orbit.enabled = mode === 'explore' && enabled
    pointerSteering = false
    pointerId = null
    dragTurn = 0
    dragClimb = 0
    if (mode === 'walk' && prev !== 'walk') {
      camera.position.set(77, 0, 29)
      applyWalkBounds()
      camera.lookAt(ANCHORS.peak.x, getHeight(ANCHORS.peak.x, ANCHORS.peak.z), ANCHORS.peak.z)
      euler.setFromQuaternion(camera.quaternion)
    }
    if (mode === 'flight' && prev !== 'flight') {
      const from =
        prev === 'explore'
          ? new THREE.Vector3(orbit.target.x, 0, orbit.target.z)
          : camera.position.clone()
      const ground = getHeight(from.x, from.z)
      from.y = ground + 2.5
      flight.enter(from, headingFromCamera())
    }
    onModeChange?.(mode)
  }

  const setExplorePose = (position: THREE.Vector3, target: THREE.Vector3) => {
    camera.position.copy(position)
    orbit.target.copy(target)
    orbit.update()
  }

  const updateFlightInput = () => {
    let turn =
      (keys.has('KeyA') || keys.has('ArrowLeft') ? 1 : 0) +
      (keys.has('KeyD') || keys.has('ArrowRight') ? -1 : 0)
    let climb =
      (keys.has('KeyW') || keys.has('ArrowUp') ? 1 : 0) +
      (keys.has('KeyS') || keys.has('ArrowDown') ? -1 : 0)
    turn = THREE.MathUtils.clamp(turn + dragTurn, -1, 1)
    climb = THREE.MathUtils.clamp(climb + dragClimb, -1, 1)
    dragTurn = 0
    dragClimb = 0
    flight.setInput({
      forward: 1,
      turn,
      climb,
      boost: keys.has('ShiftLeft') || keys.has('ShiftRight'),
    })
  }

  const update = (dt: number) => {
    if (!enabled) return
    if (mode === 'explore') {
      if (flying) {
        const alpha = 1 - Math.exp(-dt * 5)
        camera.position.lerp(flyCamera, alpha)
        orbit.target.lerp(flyTarget, alpha)
        if (
          camera.position.distanceToSquared(flyCamera) < 0.01 &&
          orbit.target.distanceToSquared(flyTarget) < 0.01
        ) {
          camera.position.copy(flyCamera)
          orbit.target.copy(flyTarget)
          flying = false
        }
      }
      clampExploreTarget()
      orbit.update()
      return
    }
    if (mode === 'flight') {
      updateFlightInput()
      flight.update(dt, camera)
      return
    }
    if (mode !== 'walk') return

    const speed = keys.has('ShiftLeft') || keys.has('ShiftRight') ? 14 : 7
    forward.set(0, 0, -1).applyQuaternion(camera.quaternion)
    forward.y = 0
    forward.normalize()
    right.crossVectors(forward, new THREE.Vector3(0, 1, 0)).normalize()

    if (keys.has('KeyW')) camera.position.addScaledVector(forward, speed * dt)
    if (keys.has('KeyS')) camera.position.addScaledVector(forward, -speed * dt)
    if (keys.has('KeyA')) camera.position.addScaledVector(right, -speed * dt)
    if (keys.has('KeyD')) camera.position.addScaledVector(right, speed * dt)
    applyWalkBounds()
  }

  const dispose = () => {
    orbit.dispose()
    flight.dispose()
    window.removeEventListener('keydown', onKeyDown)
    window.removeEventListener('keyup', onKeyUp)
    dom.removeEventListener('click', onClick)
    document.removeEventListener('pointerlockchange', onLockChange)
    document.removeEventListener('mousemove', onMouseMove)
    dom.removeEventListener('pointerdown', onPointerDown)
    dom.removeEventListener('pointermove', onPointerMove)
    dom.removeEventListener('pointerup', endPointerSteer)
    dom.removeEventListener('pointercancel', endPointerSteer)
  }

  const api: ModeControls = {
    get mode() {
      return mode
    },
    setMode,
    update,
    dispose,
    setExplorePose,
    getExploreTarget: () => orbit.target,
    getFocusPoint: () => (mode === 'flight' ? flight.position : orbit.target),
    getFlight: () => flight,
    focusOn,
    frameOn,
    resetNorth,
    topDown,
    get enabled() {
      return enabled
    },
    set enabled(v: boolean) {
      enabled = v
      orbit.enabled = enabled && mode === 'explore'
      if (!enabled && document.pointerLockElement === dom) document.exitPointerLock()
    },
    get onModeChange() {
      return onModeChange
    },
    set onModeChange(fn) {
      onModeChange = fn
    },
  }
  return api
}
